import React from 'react';
import Icon from '../AppIcon';

const StatusIndicatorBadge = ({ 
  status, 
  type = 'default', 
  size = 'md', 
  showIcon = true, 
  tooltip = '',
  className = '' 
}) => {
  
  const getStatusConfig = (status, type) => {
    // Normalize status to lowercase string
    const normalized = String(status).toLowerCase();
    const configs = {
      pending: {
        label: 'Pending', bgColor: 'bg-warning-100', textColor: 'text-warning-800', borderColor: 'border-warning-200', icon: 'Clock'
      },
      approved: {
        label: 'Approved', bgColor: 'bg-success-100', textColor: 'text-success-800', borderColor: 'border-success-200', icon: 'CheckCircle'
      },
      pending_quota_review: {
        label: 'Pending Review',
        bgColor: 'bg-warning-100',
        textColor: 'text-warning-800',
        borderColor: 'border-warning-200',
        icon: 'Clock'
      },
      ready_for_selection: {
        label: 'Ready for Selection',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200',
        icon: 'ClipboardCheck'
      },
      selection_submitted: {
        label: 'Selection Submitted',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        borderColor: 'border-purple-200',
        icon: 'Send'
      },
      selection_approved: {
        label: 'Selection Approved',
        bgColor: 'bg-success-100',
        textColor: 'text-success-800',
        borderColor: 'border-success-200',
        icon: 'CheckCircle'
      },
      selection_rejected: {
        label: 'Selection Rejected',
        bgColor: 'bg-error-100',
        textColor: 'text-error-800',
        borderColor: 'border-error-200',
        icon: 'XCircle'
      },
      rejected: {
        label: 'Rejected', bgColor: 'bg-error-100', textColor: 'text-error-800', borderColor: 'border-error-200', icon: 'XCircle'
      },
      resubmit: {
        label: 'Resubmit', bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200', icon: 'RefreshCw'
      },
      amended: {
        label: 'Amended', bgColor: 'bg-purple-100', textColor: 'text-purple-800', borderColor: 'border-purple-200', icon: 'Edit'
      },
      draft: {
        label: 'Draft', bgColor: 'bg-secondary-100', textColor: 'text-secondary-800', borderColor: 'border-secondary-200', icon: 'Edit3'
      },
      // Numeric string status mapping
      '0': { label: 'Draft', bgColor: 'bg-secondary-100', textColor: 'text-secondary-800', borderColor: 'border-secondary-200', icon: 'Edit3' },
      '1': { label: 'Pending', bgColor: 'bg-warning-100', textColor: 'text-warning-800', borderColor: 'border-warning-200', icon: 'Clock' },
      '2': { label: 'Approved', bgColor: 'bg-success-100', textColor: 'text-success-800', borderColor: 'border-success-200', icon: 'CheckCircle' },
      '3': { label: 'Rejected', bgColor: 'bg-error-100', textColor: 'text-error-800', borderColor: 'border-error-200', icon: 'XCircle' },
      '4': { label: 'Resubmit', bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200', icon: 'RefreshCw' },
      // Biodata Status
      expired: {
        label: 'Expired',
        bgColor: 'bg-secondary-100',
        textColor: 'text-secondary-800',
        borderColor: 'border-secondary-200',
        icon: 'AlertTriangle'
      },
      active: {
        label: 'Active',
        bgColor: 'bg-success-100',
        textColor: 'text-success-800',
        borderColor: 'border-success-200',
        icon: 'CheckCircle'
      },
      inactive: {
        label: 'Inactive',
        bgColor: 'bg-secondary-100',
        textColor: 'text-secondary-800',
        borderColor: 'border-secondary-200',
        icon: 'Minus'
      },
      // User Status
      online: {
        label: 'Online',
        bgColor: 'bg-success-100',
        textColor: 'text-success-800',
        borderColor: 'border-success-200',
        icon: 'Circle'
      },
      offline: {
        label: 'Offline',
        bgColor: 'bg-secondary-100',
        textColor: 'text-secondary-800',
        borderColor: 'border-secondary-200',
        icon: 'Circle'
      },
      // Priority Status
      high: {
        label: 'High Priority',
        bgColor: 'bg-error-100',
        textColor: 'text-error-800',
        borderColor: 'border-error-200',
        icon: 'AlertTriangle'
      },
      medium: {
        label: 'Medium Priority',
        bgColor: 'bg-warning-100',
        textColor: 'text-warning-800',
        borderColor: 'border-warning-200',
        icon: 'AlertCircle'
      },
      low: {
        label: 'Low Priority',
        bgColor: 'bg-accent-100',
        textColor: 'text-accent-800',
        borderColor: 'border-accent-200',
        icon: 'Info'
      },
      // Document Status
      complete: {
        label: 'Complete',
        bgColor: 'bg-success-100',
        textColor: 'text-success-800',
        borderColor: 'border-success-200',
        icon: 'CheckCircle'
      },
      incomplete: {
        label: 'Incomplete',
        bgColor: 'bg-warning-100',
        textColor: 'text-warning-800',
        borderColor: 'border-warning-200',
        icon: 'AlertCircle'
      },
      // Default
      default: {
        label: status || 'Unknown', bgColor: 'bg-secondary-100', textColor: 'text-secondary-800', borderColor: 'border-secondary-200', icon: 'Circle'
      }
    };
    return configs[normalized] || configs[status] || configs.default;
  };

  const getSizeClasses = (size) => {
    const sizeMap = {
      sm: {
        container: 'px-2 py-0.5 text-xs',
        icon: 12,
        gap: 'space-x-1'
      },
      md: {
        container: 'px-2.5 py-1 text-xs',
        icon: 14,
        gap: 'space-x-1.5'
      },
      lg: {
        container: 'px-3 py-1.5 text-sm',
        icon: 16,
        gap: 'space-x-2'
      }
    };
    return sizeMap[size] || sizeMap.md;
  };

  const config = getStatusConfig(status, type);
  const sizeClasses = getSizeClasses(size);

  const badgeClasses = `
    inline-flex items-center font-medium rounded-full border
    ${config.bgColor} ${config.textColor} ${config.borderColor}
    ${sizeClasses.container} ${sizeClasses.gap}
    transition-all duration-150 ease-in-out
    ${className}
  `.trim();

  const BadgeContent = () => (
    <span className={badgeClasses}>
      {showIcon && (
        <Icon 
          name={config.icon} 
          size={sizeClasses.icon} 
          className="flex-shrink-0"
        />
      )}
      <span className="whitespace-nowrap">{config.label}</span>
    </span>
  );

  // If tooltip is provided, wrap in a tooltip container
  if (tooltip) {
    return (
      <div className="relative group inline-block">
        <BadgeContent />
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-secondary-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-1001 pointer-events-none">
          {tooltip}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-secondary-900"></div>
        </div>
      </div>
    );
  }

  return <BadgeContent />;
};

export default StatusIndicatorBadge;
